import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Select,
  MenuItem,
  Card,
  CardContent,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
} from "@mui/material";
import { Description, PictureAsPdf, VideoLibrary } from "@mui/icons-material";
import axios from "axios";
import { useTable, useSortBy, usePagination } from "react-table";
 
const Tracking = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [selectedProject, setSelectedProject] = useState(null);
  const [message, setMessage] = useState("");
 
  // Fetch data from API
  useEffect(() => {
    axios
      .get("https://puneexpobackend.tdtlworld.com/api/all_projects/")
      .then((response) => {
        setProjects(response.data.Idea_Submission || []);
        setLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching projects:", error);
        setLoading(false);
      });
  }, []);
 
  const handleSupportProject = (project) => {
    const investorName = localStorage.getItem("userEmail");
    const defaultMessage = `Hello ${project.title},\n\nI am ${investorName}. I am interested in supporting your project. Please let me know how we can proceed further.`;
    setSelectedProject(project);
    setMessage(defaultMessage);
    setOpen(true);
  };
 
  const handleSendMessage = () => {
    if (!message.trim()) {
      alert("Message cannot be empty.");
      return;
    }
 
    const investorId = localStorage.getItem("user_id");
    const authToken = localStorage.getItem("authToken");
 
    const payload = {
      investor_id: investorId,
      idea_id: selectedProject.id,
      message: message,
    };
 
    axios
      .post("https://puneexpobackend.tdtlworld.com/api/fund_project/", payload, {
        headers: {
          Authorization: authToken,
        },
      })
      .then((response) => {
        alert(response.data.msg);
        setOpen(false);
        setMessage("");
      })
      .catch((error) => {
        console.error("Error funding project:", error);
        alert("Failed to send the message. Please try again later.");
      });
  };
 
  const columns = React.useMemo(
    () => [
      {
        Header: "Title",
        accessor: "title",
      },
      {
        Header: "Description",
        accessor: "description",
      },
      {
        Header: "Submit Status",
        accessor: "submit",
        Cell: ({ value }) => (value ? "Yes" : "No"),
      },
      {
        Header: "Idea",
        accessor: "idea",
        Cell: ({ value }) => (
          <Tooltip title={value ? "View Idea" : "No Idea Available"}>
            <Button
              variant="text"
              startIcon={<Description color={value ? "success" : "disabled"} />}
              disabled={!value}
              sx={{
                color: value ? "green" : "gray",
              }}
            >
              Idea
            </Button>
          </Tooltip>
        ),
      },
      {
        Header: "PPT",
        accessor: "ppt",
        Cell: ({ value }) => (
          <Tooltip title={value ? "View PPT" : "No PPT Available"}>
            <Button
              variant="text"
              startIcon={<PictureAsPdf color={value ? "error" : "disabled"} />}
              disabled={!value}
              sx={{
                color: value ? "red" : "gray",
              }}
            >
              PPT
            </Button>
          </Tooltip>
        ),
      },
      {
        Header: "Video File",
        accessor: "video_file",
        Cell: ({ value }) => (
          <Tooltip title={value ? "View Video" : "No Video Available"}>
            <Button
              variant="text"
              startIcon={
                <VideoLibrary color={value ? "primary" : "disabled"} />
              }
              disabled={!value}
              sx={{
                color: value ? "blue" : "gray",
              }}
            >
              Video
            </Button>
          </Tooltip>
        ),
      },
      {
        Header: "Action",
        accessor: "id",
        Cell: ({ row }) => (
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleSupportProject(row.original)}
            sx={{
              background: "linear-gradient(45deg, #FE6B8B 30%, #FF8E53 90%)",
              color: "white",
              fontWeight: "bold",
              padding: "8px 16px",
              borderRadius: "20px",
              "&:hover": {
                background: "linear-gradient(45deg, #FE6B8B 60%, #FF8E53 100%)",
              },
            }}
          >
            Support Project
          </Button>
        ),
      },
    ],
    []
  );
 
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { pageIndex, pageSize },
    setPageSize,
    canPreviousPage,
    canNextPage,
    gotoPage,
    pageCount,
    previousPage,
    nextPage,
  } = useTable(
    {
      columns,
      data: projects,
      initialState: {
        pageIndex: 0,
        pageSize: 5,
      },
    },
    useSortBy,
    usePagination
  );
 
  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4, mb: 4, textAlign: "center" }}>
        <CircularProgress />
        <Typography variant="h6" mt={2}>
          Loading Projects...
        </Typography>
      </Container>
    );
  }
 
  return (
    <Container maxWidth="lg" sx={{ mt: 11, mb: 4 }}>
       <Typography
        variant="h4"
        sx={{
          mb: 4,
          fontWeight: "bold",
          color: "#37474f",
          textAlign: "center",
        }}
      >
Explore Projects and support      </Typography>
      <Card
        sx={{
          boxShadow: 3,
          borderRadius: 3,
          background: "linear-gradient(to bottom, #f0f0f5, #ffffff)",
        }}
      >
        <CardContent>
          <TableContainer>
            <Table {...getTableProps()} sx={{ minWidth: 650 }}>
              <TableHead>
                {headerGroups.map((headerGroup) => (
                  <TableRow {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <TableCell {...column.getHeaderProps(column.getSortByToggleProps())}>
                        {column.render("Header")}
                        <span>
                          {column.isSorted
                            ? column.isSortedDesc
                              ? " 🔽"
                              : " 🔼"
                            : ""}
                        </span>
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </TableHead>
              <TableBody {...getTableBodyProps()}>
                {rows.map((row) => {
                  prepareRow(row);
                  return (
                    <TableRow {...row.getRowProps()}>
                      {row.cells.map((cell) => (
                        <TableCell {...cell.getCellProps()}>
                          {cell.render("Cell")}
                        </TableCell>
                      ))}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {/* Pagination controls */}
          <Box mt={2} textAlign="center">
            <Button
              onClick={() => gotoPage(0)}
              disabled={!canPreviousPage}
              sx={{ marginRight: 1 }}
            >
              {"<<"}
            </Button>
            <Button onClick={() => previousPage()} disabled={!canPreviousPage}>
              Previous
            </Button>
            <Button onClick={() => nextPage()} disabled={!canNextPage}>
              Next
            </Button>
            <Button
              onClick={() => gotoPage(pageCount - 1)}
              disabled={!canNextPage}
              sx={{ marginLeft: 1 }}
            >
              {">>"}
            </Button>
            <div>
              Page{" "}
              <strong>
                {pageIndex + 1} of {pageCount}
              </strong>
            </div>
            <div>
              <span>Rows per page: </span>
              <Select
                value={pageSize}
                onChange={(e) => setPageSize(Number(e.target.value))}
                sx={{ width: 100 }}
              >
                {[5, 10, 20].map((size) => (
                  <MenuItem key={size} value={size}>
                    {size}
                  </MenuItem>
                ))}
              </Select>
            </div>
          </Box>
        </CardContent>
      </Card>
 
      {/* Dialog for sending message */}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Send a Message</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Send a message to the project owner to express your interest in funding their project. You can edit the message below.
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Message"
            type="text"
            fullWidth
            multiline
            rows={4}
            value={message}
            onChange={(e) => setMessage(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpen(false)}>Cancel</Button>
          <Button onClick={handleSendMessage} color="primary">
            Send
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};
 
export default Tracking;
 